import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { Pagination } from '@/types/api/response.types';
import {
  getClientVariation,
  getClientVariations,
  getClientVariationCount,
  downloadClientVariationDocument,
  uploadClientVariationDocuments,
  updateClientVariationStatus,
  updateClientVariation,
  deleteClientVariationDocument,
  deleteClientVariation,
  createClientVariation,
  downloadProofOfPayment,
  logPayment,
} from '@/api/client-variation.service';
import { Status } from '@/common/constants';
import {
  ClientVariation,
  ClientVariationItem,
  ClientVariationParams,
  UpdateClientVariationBody,
  UpdateClientVariationStatus,
  LogClientVariationPaymentBody,
  ClientVariationDocumentDownloadResponse,
} from '@/types/api/client-variations.types';
import { useBoqStore } from '@/stores/boqs.store';
import { useToastStore } from '@/stores/utils/toast.store';
import { DocumentUploadResponse } from '@/types/api/orders.types';
import { documentUploadToast } from '@/common/utils/general.utils';
import { toRefs } from 'vue';
import { useTotalsStore } from '@/stores/utils/totals.store';

const { t } = i18n.global;
export const useClientVariationStore = defineStore('client-variations', {
  state: () => ({
    allClientVariations: [] as ClientVariation[],
    paginatedClientVariations: [] as ClientVariation[],
    clientVariation: {} as ClientVariation,
    clientVariationPagination: {} as Pagination,
    clientVariationCount: 0 as number,
    clientVariationDoc: {} as DocumentUploadResponse,
    pendingClientVariationsForUser: [] as ClientVariation[],
    draftClientVariationsForUser: [] as ClientVariation[],
    item: {} as ClientVariationItem,
    proofOfPaymentDocs: {} as ClientVariationDocumentDownloadResponse,
    dontShowConfirmDialogAgain: localStorage.getItem('dontShowConfirmDialogAgain') ?? null,

    clientVariationParams: {
      pageNo: 1,
      pageSize: 10,
      orderByDesc: 'CreatedAt',
    } as ClientVariationParams,
  }),

  actions: {
    async getClientVariations(getAll: boolean, params: ClientVariationParams) {
      const response = getAll
        ? await getClientVariations(params)
        : await getClientVariations({ ...this.clientVariationParams, ...params });

      if (getAll) {
        this.allClientVariations = response.data.data;
      } else {
        const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
        this.clientVariationPagination = { pageCount, totalCount, pageNo, pageSize };

        this.clientVariationParams = {
          ...this.clientVariationParams,
          ...params,
          pageNo: pageNo,
        };

        this.paginatedClientVariations = response.data.data;
      }
    },
    async getClientVariation(id: string) {
      const response = await getClientVariation(id);
      this.clientVariation = response.data.data;
    },
    async getPendingClientVariationByAssignedUser(userId: string, orgId: string, siteId?: string) {
      const response = await getClientVariations({
        organisationId: orgId,
        status: Status.pending,
        assignedUserId: userId,
        siteId,
      });

      this.pendingClientVariationsForUser = response.data.data;
      return response.data.data;
    },

    async getDraftClientVariationByAssignedUser(userId: string, orgId: string, siteId?: string) {
      const response = await getClientVariations({
        organisationId: orgId,
        status: Status.draft,
        assignedUserId: userId,
        siteId,
      });
      this.draftClientVariationsForUser = response.data.data;
      return response.data.data;
    },
    async createClientVariation(id: string, data: ClientVariation) {
      const response = await createClientVariation(id, data);
      this.clientVariation = response.data.data;
    },
    async updateClientVariation(id: string, data: UpdateClientVariationBody) {
      const response = await updateClientVariation(id, data);

      this.paginatedClientVariations = this.paginatedClientVariations.map((clientVariation) =>
        clientVariation.id === id ? response.data.data : clientVariation,
      );
      this.clientVariation = response.data.data;
    },
    async deleteClientVariation(id: string) {
      const toastStore = useToastStore();
      const response = await deleteClientVariation(id);

      if (response.status === 204) {
        toastStore.showToast(
          'success',
          t('clients.actionVariationToast.deleted'),
          t('clients.actionVariationToast.body', {
            action: t('clients.actionVariationToast.updated').toLowerCase(),
          }),
        );
      }
    },
    async updateClientVariationStatus(
      id: string,
      data: UpdateClientVariationStatus,
      siteId?: string,
    ) {
      const boqStore = useBoqStore();
      const response = await updateClientVariationStatus(id, data);

      this.paginatedClientVariations = this.paginatedClientVariations.map((clientVariation) =>
        clientVariation.id === id ? response.data.data : clientVariation,
      );
      this.clientVariation = response.data.data;

      if (siteId && boqStore.hasBoqForSite(siteId)) {
        await boqStore.getBOQ(siteId, true);
      }
    },
    async getClientVariationCount(orgId: string) {
      const { clientVariationsByOrgCount } = toRefs(useTotalsStore());
      const response = await getClientVariationCount(orgId);
      clientVariationsByOrgCount.value = response.data.data.pendingAssignedCount;
      this.clientVariationCount = response.data.data.pendingAssignedCount;
    },
    async uploadDocuments(id: string, data: FormData) {
      const response = await uploadClientVariationDocuments(id, data);

      documentUploadToast({
        responseCode: response.status,
        title: t('documents.toasts.successTitle'),
        description: t('documents.toasts.successSubtitle'),
        showToast: false,
        onSuccess: async () => {
          await this.getClientVariation(id);
        },
      });
    },
    async logPayment(id: string, data: LogClientVariationPaymentBody) {
      const toastStore = useToastStore();
      const response = await logPayment(id, data);

      this.paginatedClientVariations = this.paginatedClientVariations.map((clientVariation) =>
        clientVariation.id === id ? response.data.data : clientVariation,
      );
      this.clientVariation = response.data.data;

      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t('clients.logPaymentToast.title'),
          t('clients.logPaymentToast.description'),
        );
      }
    },
    async downloadProofOfPayment(variationId: string, paymentId: string) {
      const response = await downloadProofOfPayment(variationId, paymentId);
      this.proofOfPaymentDocs = response.data.data;
    },
    async deleteDocument(variationId: string, docId: string) {
      await deleteClientVariationDocument(variationId, docId);
    },
    async downloadDocument(variationId: string, docId: string) {
      const response = await downloadClientVariationDocument(variationId, docId);

      if (response.status === 200) {
        return response.data;
      }
    },
    clearPendingAndDraftVariations() {
      this.pendingClientVariationsForUser = [];
      this.draftClientVariationsForUser = [];
    },
    setDialogDecision(decision: boolean) {
      this.dontShowConfirmDialogAgain = JSON.stringify(decision);
      localStorage.setItem('dontShowConfirmDialogAgain', JSON.stringify(decision));
    },
  },
});
